import React from "react";
import _ from "lodash";
import { Trans } from "react-i18next";

const components = { b: <b /> };

export const getGradeScaleSetupTabOptions = ({ t }) => {
  return [
    {
      value: "DEFAULT_GRADE_SCALE",
      label: t("academicSetup:default_grade_boundary"),
      subtext: t("academicSetup:default_grade_boundary_subtext"),
    },
    {
      value: "GRADING_PERIOD_WISE_CONFIGURATION",
      label: t("academicSetup:custom_grade_boundaries"),
      subtext: t("academicSetup:custom_grade_boundaries_subtext"),
    },
  ];
};

export const validateGradeScaleFields = _.memoize(
  ({ t, fieldValues, isFinalGradeManuallyAssigned }) => {
    let errors = {};

    const filteredFieldValues = _.filter(fieldValues, fieldValue => {
      return !_.isEmpty(fieldValue?.scoreValuesId);
    });

    for (let i = 0; i < _.size(filteredFieldValues); i++) {
      const currentVal = filteredFieldValues[i] || {};
      const nextVal = filteredFieldValues[i + 1] || {};

      if (_.isEmpty(currentVal?.label)) {
        errors = {
          ...errors,
          [`${currentVal?.id}-label`]: {
            errorMsg: t("common:required"),
          },
        };
      }

      if (!isFinalGradeManuallyAssigned) {
        if (
          Number(currentVal?.percentageWeight) <=
          Number(nextVal?.percentageWeight)
        ) {
          errors = {
            ...errors,
            [`${currentVal?.id}`]: {
              errorMsg: t("common:value_cant_be_more_than", {
                label: nextVal?.percentageWeight,
              }),
            },
          };
        }
      }
    }

    return errors;
  },
  params => JSON.stringify(params)
);

export const getGradeScaleCreateUpdateInput = ({
  curriculumProgramId,
  termId = null,
  subjectId = null,
  gradeId = null,
  gradeScaleObj,
  academicCriteriaValues,
  isDefaultGradeScale,
  isCategoryGradeScale,
  isCreateMode,
  isFinalGradeManuallyAssigned,
  isCustomizingDefaultGradeScale,
}) => {
  const associatedEntities = [];

  if (termId) {
    associatedEntities.push({
      type: "ACADEMIC_TERM",
      id: termId,
    });
  }

  if (subjectId) {
    associatedEntities.push({
      type: "SUBJECT",
      id: subjectId,
    });
  }

  if (gradeId) {
    associatedEntities.push({
      type: "GRADE",
      id: gradeId,
    });
  }

  const fieldValues = _.map(_.get(gradeScaleObj, "fieldValues"), fieldValue => {
    return {
      ...fieldValue,
      percentageWeight: parseFloat(fieldValue?.percentageWeight),
    };
  });

  const gradeScaleFieldValues = isFinalGradeManuallyAssigned
    ? fieldValues
    : _.orderBy(fieldValues, "percentageWeight", "desc");
  const weights = _.sortBy(_.map(gradeScaleFieldValues, "percentageWeight"));

  const addedCategories = [];
  const removedCategories = [];
  const updatedCategories = [];

  _.forEach(gradeScaleFieldValues, (value, index) => {
    const { id, label, percentageWeight, scoreValuesId } = value || {};

    const weightIndex = _.indexOf(weights, percentageWeight);
    const maxValue = weights[weightIndex + 1] || 100;

    const scoreValueObj = {
      minValue: percentageWeight,
      maxValue: maxValue,
      weightageType: "PERCENTAGE",
      valueType: "RANGE",
    };

    const categoryObj = {
      label,
      displaySequence: index + 1,
    };

    if (_.includes(id, "NEW") || isCustomizingDefaultGradeScale) {
      addedCategories.push({ ...categoryObj, values: [scoreValueObj] });
    } else if (
      !_.isEmpty(_.find(academicCriteriaValues, value => value?.id == id))
    ) {
      const updatedValue = { id: scoreValuesId, ...scoreValueObj };

      updatedCategories.push({
        id,
        ...categoryObj,
        values: !isCreateMode
          ? {
              updatedCategorieValues: [updatedValue],
            }
          : [updatedValue],
      });
    }
  });

  _.forEach(academicCriteriaValues, value => {
    if (
      _.isEmpty(
        _.find(
          gradeScaleFieldValues,
          fieldValue => fieldValue?.id === value?.id
        )
      )
    ) {
      removedCategories.push(value?.id);
    }
  });

  if (!isCreateMode) {
    return {
      id: gradeScaleObj?.id,
      label: "Overall grade",
      categories: {
        addedCategories,
        removedCategories,
        updatedCategories,
      },
    };
  }

  const subType = isCategoryGradeScale
    ? "CATEGORY_GRADE_SCALE"
    : isDefaultGradeScale
    ? "DEFAULT"
    : "CUSTOM";

  return {
    type: "LOCAL_GRADE",
    label: "Overall grade",
    subType,
    associatedEntites: associatedEntities,
    categories: addedCategories,
    curriculumProgramId,
  };
};

export const getCustomGradeScalesCount = _.memoize(
  ({ rootNodes }) => {
    let count = 0;
    _.forEach(rootNodes, rootNode => {
      const pointAssessmentSets = _.get(
        rootNode,
        "pointAssessmentSets.edges",
        []
      );
      count += _.size(pointAssessmentSets);
    });
    return count;
  },
  params => JSON.stringify(params)
);

export const getGradeScaleSetByGradeId = _.memoize(
  ({ organizationGradeScaleSets, gradeId }) => {
    return _.get(
      _.first(
        _.filter(organizationGradeScaleSets, currentGradeScaleSet => {
          return _.find(
            _.get(currentGradeScaleSet, "node.associatedEntities"),
            associatedEntity => {
              return (
                _.get(associatedEntity, "__typename") === "Grade" &&
                _.isEqual(_.get(associatedEntity, "id"), gradeId)
              );
            }
          );
        })
      ),
      "node",
      {}
    );
  },
  params => JSON.stringify(params)
);

export const getGradeScaleDropdownOptions = _.memoize(
  ({ t, isSubjectSettingsContext = false, isGradingPeriodUnlocked = true }) => {
    const options = [
      {
        key: "EDIT",
        label: t("common:edit"),
        disabled: !isGradingPeriodUnlocked,
        disabledTooltip: !isGradingPeriodUnlocked
          ? t(
              "academicSetup:grading_period_is_locked_grading_configurations_cannot_be_edited"
            )
          : null,
      },
      {
        key: "RESET_TO_DEFAULT",
        label: t("common:reset_to_default"),
        disabled: !isGradingPeriodUnlocked,
        disabledTooltip: !isGradingPeriodUnlocked
          ? t(
              "academicSetup:grading_period_is_locked_grading_configurations_cannot_be_edited"
            )
          : null,
      },
    ];

    if (!isSubjectSettingsContext) {
      options.push(
        {
          key: "d-1",
          isDivider: true,
        },
        {
          key: "BULK_DUPLICATE_TO_OTHER_SUBJECTS",
          label: t(
            "academicSetup:duplicate_to_other_subjects_and_grade_levels"
          ),
        }
      );
    }

    return options;
  },
  params => JSON.stringify(params)
);

export const getConfirmationModalContentByAction = _.memoize(
  ({
    t,
    action,
    gradeLabel,
    subjectName,
    customGradeScalesCount,
    possibleGradeScalesCount,
  }) => {
    switch (action) {
      case "RESET_CONFIRMATION": {
        const bodyContent = (
          <Trans
            i18nKey="academicSetup:reset_grade_boundary_text_with_subject_grade_label"
            components={components}
            values={{ subjectLabel: subjectName, gradeLabel }}
          >
            You are resetting the
            <b>
              {{ subjectLabel: subjectName }}-{{ gradeLabel: gradeLabel }}
            </b>
            grade boundary to default. This will cause the
            <b>
              overall grade to be recalculated in the gradebook and in unlocked
              progress reports
            </b>
            for this subject and grade.
          </Trans>
        );

        return {
          title: t("common:reset_to_default"),
          bodyContent,
          confirmationText: t("common:i_understand_and_would_like_to_proceed"),
          saveButtonText: t("common:reset"),
        };
      }

      case "SAVE_CONFIRMATION": {
        const bodyContent = (
          <Trans
            i18nKey="academicSetup:saving_default_grade_boundary_with_count"
            components={components}
            values={{
              count: possibleGradeScalesCount - customGradeScalesCount,
            }}
          >
            You are modifying the default grade boundary. This will
            automatically update the default grade boundary currently being used
            in
            <b>
              {{ count: possibleGradeScalesCount - customGradeScalesCount }}
              grades
            </b>
            within your programme.
          </Trans>
        );

        const confirmationText = (
          <Trans
            i18nKey="academicSetup:saving_default_grade_boundary_confirmation"
            components={components}
          >
            I understand that this will also cause the
            <b>
              overall grade to be recalculated in the gradebook and in unlocked
              progress reports.
            </b>
          </Trans>
        );

        return {
          title: t("common:save_with_label", {
            label: t("academicSetup:default_grade_boundary"),
          }),
          bodyContent,
          confirmationText,
          saveButtonText: t("common:save_and_update"),
        };
      }
      default:
        return {};
    }
  },
  params => JSON.stringify(params)
);

export const GRADE_SCALE_SUB_TYPES_INFO = {
  DEFAULT: {
    labelLocale: "common:default",
    color: "neutral",
  },
  CUSTOM: {
    labelLocale: "common:custom",
    color: "yellow",
  },
};

export const checkIfGradeScaleIsUpdated = _.memoize(
  ({ gradeScaleSet, gradeScaleObj }) => {
    const { fieldValues } = gradeScaleObj || {};
    const { categories } = gradeScaleSet || {};

    if (_.size(fieldValues) !== _.size(categories)) return true;

    let isUpdated = false;

    _.forEach(categories, category => {
      const { id: categoryId, values, label: categoryLabel } = category || {};
      const { minValue: scoreValue } = _.first(values) || {};

      const fieldValue = _.find(fieldValues, currentFieldValue => {
        return (
          _.isEqual(currentFieldValue?.id, categoryId) &&
          _.isEqual(currentFieldValue?.percentageWeight, scoreValue) &&
          _.isEqual(currentFieldValue?.label, categoryLabel)
        );
      });

      if (!fieldValue) {
        isUpdated = true;
        return false;
      }
    });

    return isUpdated;
  },
  params => JSON.stringify(params)
);
